import { z } from "zod"
import {
  identityIdSchema,
  platformApprovalIdSchema,
  platformOperatorIdSchema,
  platformRoleAssignmentIdSchema,
  platformRoleIdSchema,
} from "./identifiers.js"

const unique = (values: readonly string[]): boolean =>
  new Set(values).size === values.length

export const platformOperatorStateV1Schema = z.enum([
  "invited",
  "active",
  "suspended",
  "deactivated",
])

export const platformEnvironmentV1Schema = z.enum([
  "development",
  "internal",
  "canary",
  "production",
])

export const platformRoleGroupV1Schema = z.enum([
  "ownership",
  "operations",
  "support",
  "commercial",
  "security_audit",
])

export const platformCapabilityV1Schema = z.enum([
  "workforce.operator.invite",
  "workforce.operator.activate",
  "workforce.operator.suspend",
  "workforce.operator.deactivate",
  "workforce.role.assign",
  "workforce.role.revoke",
  "workforce.role.approve",
  "workforce.access_review.perform",
  "workforce.policy.publish",
  "tenant.directory.read",
  "tenant.lifecycle.transition",
  "tenant.lifecycle.delete_approve",
  "tenant.provision.execute",
  "tenant.domain.manage",
  "tenant.restore.execute",
  "tenant.restore.approve",
  "tenant.backup.read",
  "tenant.backup.execute",
  "tenant.export.execute",
  "tenant.delete.execute",
  "tenant.health.read",
  "commercial.plan.manage",
  "commercial.subscription.manage",
  "commercial.adjustment.issue",
  "commercial.adjustment.approve",
  "commercial.report.read",
  "support.case.manage",
  "support.grant.request",
  "support.grant.approve",
  "support.grant.use",
  "support.grant.revoke",
  "support.break_glass.request",
  "support.break_glass.approve",
  "platform.communication.send",
  "platform.communication.escalate",
  "release.cohort.manage",
  "release.deploy.execute",
  "release.deploy.approve",
  "release.halt",
  "release.recovery.decide",
  "release.feature_control.manage",
  "incident.manage",
  "provider.readiness.read",
  "provider.integration.manage",
  "provider.reconciliation.retry",
  "security.review.perform",
  "security.legal_hold.manage",
  "audit.platform.read",
  "audit.platform.export",
  "audit.tenant_grant_evidence.read",
])

export type PlatformCapabilityV1 = z.infer<typeof platformCapabilityV1Schema>
export type PlatformRoleGroupV1 = z.infer<typeof platformRoleGroupV1Schema>
export type PlatformSupportScopeV1 = z.infer<typeof platformSupportScopeV1Schema>

const CAPABILITIES_BY_GROUP: Readonly<
  Record<PlatformRoleGroupV1, ReadonlySet<PlatformCapabilityV1>>
> = {
  ownership: new Set([
    "workforce.operator.invite",
    "workforce.role.assign",
    "workforce.role.revoke",
    "workforce.policy.publish",
    "tenant.directory.read",
    "commercial.report.read",
    "audit.platform.read",
  ]),
  operations: new Set([
    "tenant.directory.read",
    "tenant.lifecycle.transition",
    "tenant.provision.execute",
    "tenant.domain.manage",
    "tenant.restore.execute",
    "tenant.backup.read",
    "tenant.backup.execute",
    "tenant.export.execute",
    "tenant.delete.execute",
    "tenant.health.read",
    "release.cohort.manage",
    "release.deploy.execute",
    "release.halt",
    "release.recovery.decide",
    "release.feature_control.manage",
    "incident.manage",
    "provider.readiness.read",
    "provider.integration.manage",
    "provider.reconciliation.retry",
  ]),
  support: new Set([
    "tenant.directory.read",
    "tenant.health.read",
    "support.case.manage",
    "support.grant.request",
    "support.grant.use",
    "support.grant.revoke",
    "support.break_glass.request",
    "platform.communication.send",
    "platform.communication.escalate",
    "provider.readiness.read",
  ]),
  commercial: new Set([
    "tenant.directory.read",
    "commercial.plan.manage",
    "commercial.subscription.manage",
    "commercial.adjustment.issue",
    "commercial.report.read",
  ]),
  security_audit: new Set([
    "workforce.operator.activate",
    "workforce.operator.suspend",
    "workforce.operator.deactivate",
    "workforce.role.approve",
    "workforce.access_review.perform",
    "tenant.directory.read",
    "tenant.lifecycle.delete_approve",
    "tenant.restore.approve",
    "tenant.backup.read",
    "tenant.health.read",
    "commercial.adjustment.approve",
    "support.grant.approve",
    "support.grant.revoke",
    "support.break_glass.approve",
    "release.deploy.approve",
    "incident.manage",
    "provider.readiness.read",
    "security.review.perform",
    "security.legal_hold.manage",
    "audit.platform.read",
    "audit.platform.export",
    "audit.tenant_grant_evidence.read",
  ]),
}

export const platformLifecycleStateV1Schema = z.enum([
  "prospect",
  "trial",
  "active",
  "suspended",
  "offboarding",
  "retained",
  "deleted",
])

export const platformLifecycleTransitionLimitV1Schema = z
  .object({
    from: platformLifecycleStateV1Schema,
    to: platformLifecycleStateV1Schema,
  })
  .strict()
  .refine((transition) => transition.from !== transition.to, {
    message: "Lifecycle transition must change state.",
  })

export const platformCommercialLimitV1Schema = z
  .object({
    currency: z.string().regex(/^[A-Z]{3}$/, "Currency must be an ISO 4217 code."),
    maxAdjustmentAmountMinor: z.number().int().nonnegative().safe(),
  })
  .strict()

export const platformSupportScopeV1Schema = z.enum([
  "tenant.health.read",
  "tenant.configuration.read",
  "tenant.business_record.read",
  "tenant.workflow.retry",
  "tenant.export.download",
])

export const platformSupportSensitivityV1Schema = z.enum([
  "metadata",
  "operational",
  "financial",
  "payroll",
  "security",
])

export const platformReleaseCohortKindV1Schema = z.enum([
  "internal",
  "canary",
  "staged",
])

export const platformAuditScopeV1Schema = z.enum([
  "workforce",
  "lifecycle",
  "commercial",
  "support_access",
  "provider",
  "release",
  "incident",
  "security",
])

export const platformRoleLimitsV1Schema = z
  .object({
    environments: z.array(platformEnvironmentV1Schema).min(1).max(4),
    commercial: z.array(platformCommercialLimitV1Schema).max(32),
    lifecycle: z
      .object({
        allowedTransitions: z.array(platformLifecycleTransitionLimitV1Schema).max(32),
        deletionApprovalAllowed: z.boolean(),
      })
      .strict(),
    support: z
      .object({
        allowedScopes: z.array(platformSupportScopeV1Schema).max(5),
        maximumSensitivity: platformSupportSensitivityV1Schema,
        maximumGrantDurationMinutes: z.number().int().min(5).max(480),
        downloadAllowed: z.boolean(),
        breakGlassAllowed: z.boolean(),
      })
      .strict(),
    release: z
      .object({
        allowedCohortKinds: z.array(platformReleaseCohortKindV1Schema).max(3),
        maximumTenantCount: z.number().int().nonnegative().max(10_000),
        productionAllowed: z.boolean(),
      })
      .strict(),
    audit: z
      .object({
        readableScopes: z.array(platformAuditScopeV1Schema).max(8),
        exportAllowed: z.boolean(),
        maximumExportRows: z.number().int().nonnegative().max(100_000),
      })
      .strict(),
  })
  .strict()
  .superRefine((limits, context) => {
    const duplicateChecks: ReadonlyArray<readonly [readonly string[], string]> = [
      [limits.environments, "Environments must be unique."],
      [
        limits.commercial.map((limit) => limit.currency),
        "Commercial currencies must be unique.",
      ],
      [
        limits.lifecycle.allowedTransitions.map(
          (transition) => `${transition.from}:${transition.to}`,
        ),
        "Lifecycle transitions must be unique.",
      ],
      [limits.support.allowedScopes, "Support scopes must be unique."],
      [limits.release.allowedCohortKinds, "Release cohort kinds must be unique."],
      [limits.audit.readableScopes, "Audit scopes must be unique."],
    ]
    for (const [values, message] of duplicateChecks) {
      if (!unique(values)) context.addIssue({ code: "custom", message })
    }
    if (!limits.support.downloadAllowed
        && limits.support.allowedScopes.includes("tenant.export.download")) {
      context.addIssue({
        code: "custom",
        path: ["support", "allowedScopes"],
        message: "Tenant export scope requires download permission.",
      })
    }
    if (!limits.audit.exportAllowed && limits.audit.maximumExportRows !== 0) {
      context.addIssue({
        code: "custom",
        path: ["audit", "maximumExportRows"],
        message: "Audit export row limit must be zero when export is denied.",
      })
    }
  })

export const platformRoleDefinitionV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformRoleId: platformRoleIdSchema,
    roleCode: z
      .string()
      .regex(/^[a-z][a-z0-9_]{2,63}$/, "Platform Role code is invalid.")
      .refine(
        (code) => !["super_admin", "superuser", "root", "universal"].includes(code),
        "Universal Platform Role codes are prohibited.",
      ),
    displayName: z.string().trim().min(1).max(96),
    group: platformRoleGroupV1Schema,
    capabilities: z.array(platformCapabilityV1Schema).min(1).max(32),
    limits: platformRoleLimitsV1Schema,
    status: z.enum(["draft", "active", "retired"]),
    revision: z.number().int().positive().safe(),
  })
  .strict()
  .superRefine((role, context) => {
    if (!unique(role.capabilities)) {
      context.addIssue({
        code: "custom",
        path: ["capabilities"],
        message: "Platform capabilities must be unique.",
      })
    }
    const allowed = CAPABILITIES_BY_GROUP[role.group]
    for (const capability of role.capabilities) {
      if (!allowed.has(capability)) {
        context.addIssue({
          code: "custom",
          path: ["capabilities"],
          message: `Capability ${capability} is outside the ${role.group} group.`,
        })
      }
    }
    if (role.limits.commercial.length > 0
        && role.group !== "commercial"
        && role.group !== "security_audit") {
      context.addIssue({
        code: "custom",
        path: ["limits", "commercial"],
        message: "Commercial limits require a commercial or security/audit role.",
      })
    }
    if (role.limits.lifecycle.allowedTransitions.length > 0
        && !role.capabilities.includes("tenant.lifecycle.transition")) {
      context.addIssue({
        code: "custom",
        path: ["limits", "lifecycle", "allowedTransitions"],
        message: "Lifecycle transitions require the lifecycle capability.",
      })
    }
    if (role.limits.lifecycle.deletionApprovalAllowed
        !== role.capabilities.includes("tenant.lifecycle.delete_approve")) {
      context.addIssue({
        code: "custom",
        path: ["limits", "lifecycle", "deletionApprovalAllowed"],
        message: "Deletion approval limit must match the deletion approval capability.",
      })
    }
    if (role.limits.release.productionAllowed
        && !role.limits.environments.includes("production")) {
      context.addIssue({
        code: "custom",
        path: ["limits", "release", "productionAllowed"],
        message: "Production release authority requires the production environment.",
      })
    }
    if (role.limits.audit.exportAllowed
        !== role.capabilities.includes("audit.platform.export")) {
      context.addIssue({
        code: "custom",
        path: ["limits", "audit", "exportAllowed"],
        message: "Audit export limit must match the audit export capability.",
      })
    }
  })

const timestampSchema = z.string().datetime({ offset: true })

export const platformOperatorV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformOperatorId: platformOperatorIdSchema,
    identityId: identityIdSchema,
    displayName: z.string().trim().min(1).max(128),
    state: platformOperatorStateV1Schema,
    invitedAt: timestampSchema,
    activatedAt: timestampSchema.nullable(),
    suspendedAt: timestampSchema.nullable(),
    deactivatedAt: timestampSchema.nullable(),
    version: z.number().int().positive().safe(),
  })
  .strict()
  .superRefine((operator, context) => {
    const activated = operator.activatedAt !== null
    const suspended = operator.suspendedAt !== null
    const deactivated = operator.deactivatedAt !== null
    const valid =
      (operator.state === "invited" && !activated && !suspended && !deactivated)
      || (operator.state === "active" && activated && !suspended && !deactivated)
      || (operator.state === "suspended" && activated && suspended && !deactivated)
      || (operator.state === "deactivated" && deactivated)
    if (!valid) {
      context.addIssue({
        code: "custom",
        message: "Platform Operator timestamps do not match state.",
      })
    }
  })

export const platformRoleAssignmentV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformRoleAssignmentId: platformRoleAssignmentIdSchema,
    platformOperatorId: platformOperatorIdSchema,
    platformRoleId: platformRoleIdSchema,
    roleRevision: z.number().int().positive().safe(),
    assignmentKind: z.enum(["permanent", "temporary"]),
    environments: z.array(platformEnvironmentV1Schema).min(1).max(4),
    effectiveFrom: timestampSchema,
    expiresAt: timestampSchema.nullable(),
    status: z.enum(["pending", "active", "revoked", "expired"]),
    assignedByPlatformOperatorId: platformOperatorIdSchema,
    approvedByPlatformOperatorId: platformOperatorIdSchema.nullable(),
    approvalId: platformApprovalIdSchema.nullable(),
    version: z.number().int().positive().safe(),
  })
  .strict()
  .superRefine((assignment, context) => {
    if (!unique(assignment.environments)) {
      context.addIssue({
        code: "custom",
        path: ["environments"],
        message: "Assignment environments must be unique.",
      })
    }
    if (assignment.assignmentKind === "temporary" && assignment.expiresAt === null) {
      context.addIssue({
        code: "custom",
        path: ["expiresAt"],
        message: "Temporary Platform Role assignment requires expiry.",
      })
    }
    if (assignment.assignmentKind === "permanent" && assignment.expiresAt !== null) {
      context.addIssue({
        code: "custom",
        path: ["expiresAt"],
        message: "Permanent Platform Role assignment cannot carry expiry.",
      })
    }
    if (assignment.expiresAt !== null
        && Date.parse(assignment.expiresAt) <= Date.parse(assignment.effectiveFrom)) {
      context.addIssue({
        code: "custom",
        path: ["expiresAt"],
        message: "Assignment expiry must follow its effective time.",
      })
    }
    const approved = assignment.approvedByPlatformOperatorId !== null
      && assignment.approvalId !== null
    if (assignment.status === "active" && !approved) {
      context.addIssue({
        code: "custom",
        message: "Active Platform Role assignment requires approval.",
      })
    }
    if ((assignment.approvedByPlatformOperatorId === null)
        !== (assignment.approvalId === null)) {
      context.addIssue({
        code: "custom",
        message: "Approval actor and approval ID must be present together.",
      })
    }
    if (assignment.approvedByPlatformOperatorId
        === assignment.assignedByPlatformOperatorId) {
      context.addIssue({
        code: "custom",
        path: ["approvedByPlatformOperatorId"],
        message: "Platform Role assigner cannot approve the same assignment.",
      })
    }
  })

export const platformApprovalRuleV1Schema = z
  .object({
    actionCapability: platformCapabilityV1Schema,
    approvalCapability: platformCapabilityV1Schema,
    environments: z.array(platformEnvironmentV1Schema).min(1).max(4),
    minimumDistinctApprovers: z.number().int().min(1).max(3),
    recentAuthenticationMaximumAgeSeconds: z.number().int().min(60).max(900),
    approvalTimeToLiveSeconds: z.number().int().min(60).max(86_400),
  })
  .strict()
  .superRefine((rule, context) => {
    if (rule.actionCapability === rule.approvalCapability) {
      context.addIssue({
        code: "custom",
        message: "Action and approval capabilities must be distinct.",
      })
    }
    if (!unique(rule.environments)) {
      context.addIssue({
        code: "custom",
        path: ["environments"],
        message: "Approval environments must be unique.",
      })
    }
  })

export const platformSeparationOfDutiesPolicyV1Schema = z
  .object({
    contractVersion: z.literal(1),
    policyCode: z.string().regex(/^[a-z][a-z0-9_.-]{2,95}$/),
    revision: z.number().int().positive().safe(),
    selfApprovalAllowed: z.literal(false),
    rules: z.array(platformApprovalRuleV1Schema).min(1).max(64),
  })
  .strict()
  .superRefine((policy, context) => {
    const keys = policy.rules.flatMap((rule) =>
      rule.environments.map(
        (environment) => `${rule.actionCapability}:${environment}`,
      ),
    )
    if (!unique(keys)) {
      context.addIssue({
        code: "custom",
        path: ["rules"],
        message: "Approval rules must be unique by action and environment.",
      })
    }
  })

export type PlatformEnvironmentV1 = z.infer<typeof platformEnvironmentV1Schema>
export type PlatformOperatorStateV1 = z.infer<typeof platformOperatorStateV1Schema>
export type PlatformLifecycleStateV1 = z.infer<typeof platformLifecycleStateV1Schema>
export type PlatformRoleLimitsV1 = z.infer<typeof platformRoleLimitsV1Schema>
export type PlatformRoleDefinitionV1 = z.infer<typeof platformRoleDefinitionV1Schema>
export type PlatformOperatorV1 = z.infer<typeof platformOperatorV1Schema>
export type PlatformRoleAssignmentV1 = z.infer<
  typeof platformRoleAssignmentV1Schema
>
export type PlatformApprovalRuleV1 = z.infer<typeof platformApprovalRuleV1Schema>
export type PlatformSeparationOfDutiesPolicyV1 = z.infer<
  typeof platformSeparationOfDutiesPolicyV1Schema
>
